<template>
  <div ref="chart" class="echarts-distribution-chart" role="img" :aria-label="ariaLabel" />
</template>

<script>
import echarts, { chartAnimationDuration } from './echarts-core'

export default {
  name: 'EChartsDistributionChart',
  props: {
    categories: { type: Array, required: true },
    values: { type: Array, required: true },
    seriesName: { type: String, default: 'Orders' },
    ariaLabel: { type: String, default: 'Distribution chart' },
    isDark: { type: Boolean, default: false }
  },
  data() {
    return { chart: null, resizeObserver: null }
  },
  mounted() {
    this.$nextTick(this.initChart)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.refresh)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.chart) this.chart.dispose()
  },
  watch: {
    categories: { handler() { this.updateChart() }, deep: true },
    values: { handler() { this.updateChart() }, deep: true },
    isDark() { this.updateChart() }
  },
  methods: {
    initChart() {
      if (!this.$refs.chart || this.chart) return
      this.chart = echarts.init(this.$refs.chart, null, { renderer: 'canvas' })
      window.addEventListener('resize', this.refresh)
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(this.refresh)
        this.resizeObserver.observe(this.$refs.chart)
      }
      this.updateChart()
    },
    refresh() {
      this.$nextTick(() => this.chart && this.chart.resize())
    },
    updateChart() {
      if (!this.chart) return this.$nextTick(this.initChart)
      const textColor = this.isDark ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.6)'
      const gridColor = this.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'
      const color = this.isDark ? '#4ADE80' : '#0d2d2c'
      this.chart.setOption({
        animationDuration: chartAnimationDuration(),
        grid: { top: 18, right: 18, bottom: 40, left: 18, containLabel: true },
        tooltip: {
          trigger: 'axis',
          renderMode: 'richText',
          axisPointer: { type: 'shadow' },
          confine: true,
          backgroundColor: this.isDark ? 'rgba(35,35,35,0.96)' : 'rgba(20,20,20,0.88)',
          borderWidth: 0,
          textStyle: { color: '#fff', fontSize: 12 },
          formatter: params => {
            const point = Array.isArray(params) ? params[0] : params
            return point ? `${point.name} units\n${this.seriesName}: ${point.value}` : ''
          }
        },
        xAxis: {
          type: 'category',
          data: this.categories,
          name: 'Units in order',
          nameLocation: 'middle',
          nameGap: 28,
          nameTextStyle: { color: textColor, fontSize: 11 },
          axisLabel: { color: textColor },
          axisLine: { lineStyle: { color: gridColor } },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value',
          minInterval: 1,
          name: 'Orders',
          nameTextStyle: { color: textColor, fontSize: 11 },
          axisLabel: { color: textColor },
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: gridColor } }
        },
        series: [{
          name: this.seriesName,
          type: 'bar',
          data: this.values,
          barMaxWidth: 46,
          itemStyle: { color, borderRadius: [4, 4, 0, 0] },
          label: { show: true, position: 'top', color: textColor, fontWeight: 700 }
        }]
      }, true)
      this.refresh()
    }
  }
}
</script>

<style scoped>
.echarts-distribution-chart { width: 100%; height: 100%; min-height: 230px; }
</style>
